import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  makeStyles,
  MenuItem,
  Divider,
  Select,
} from "@material-ui/core";
import { Country, State, City } from "country-state-city";
import postalCodes from "postal-codes-js";
import { commonInlineStyles, inputFieldStyles } from "../../styles";

const useStyles = makeStyles((theme) => ({
  formContainer: { padding: "24px" },
  formTitle: { marginBottom: theme.spacing(4), fontWeight: 500 },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
  },
  button: { minWidth: 180, fontWeight: 600, fontSize: 16 },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "14px",
    marginTop: theme.spacing(0.5),
  },
  label: { marginBottom: theme.spacing(1), fontWeight: 500 },
  readOnlyValue: {
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: 4,
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
}));

const PickupAddressSection = forwardRef(
  (
    {
      initialData = {},
      onChange,
      disableEdit = false,
      editMode: initialEditMode = false,
      onEditModeChange,
      onDiscard,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      selectedPlantTab,
      plantInfo = null,
    },
    ref
  ) => {
    const classes = useStyles();
    
    // Use a ref to track which plant tab is currently loaded - persists across renders
    const loadedPlantTabRef = useRef(selectedPlantTab);
    // Track if we're syncing from props to avoid notifying parent during sync
    const isSyncingFromPropsRef = useRef(false);
    
    const [form, setForm] = useState({ ...initialData });
    const [originalData, setOriginalData] = useState({ ...initialData }); // Keep track of original data
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(initialEditMode);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    // Track country and state codes
    const [countryCode, setCountryCode] = useState("");
    const [stateCode, setStateCode] = useState("");
    // Flag to track if form has been modified since entering edit mode
    const [hasChanged, setHasChanged] = useState(false);
    // Track postal code validation errors
    const [postalCodeError, setPostalCodeError] = useState("");

    // Load initial data - only when switching to a different plant
    useEffect(() => {
      // Only reset if we're loading a different plant (switching tabs)
      if (selectedPlantTab !== undefined && selectedPlantTab !== loadedPlantTabRef.current) {
        
        setForm({ ...initialData });
        setOriginalData({ ...initialData }); // Store original data for comparison
        setErrors({});
        setHasChanged(false); // Reset hasChanged when initialData changes
        loadedPlantTabRef.current = selectedPlantTab;

        // Handle country and state conversion
        if (initialData.country) {
          const countries = Country.getAllCountries();
          const matchedCountry = countries.find(
            (c) =>
              c.name === initialData.country || c.isoCode === initialData.country
          );

          if (matchedCountry) {
            setCountryCode(matchedCountry.isoCode);

            // Load states for this country
            const countryStates = State.getStatesOfCountry(
              matchedCountry.isoCode
            );
            setStates(countryStates);

            // Find matching state
            if (initialData.state) {
              const matchedState = countryStates.find(
                (s) =>
                  s.name === initialData.state || s.isoCode === initialData.state
              );

              if (matchedState) {
                setStateCode(matchedState.isoCode);

                // Load cities for this state
                const stateCities = City.getCitiesOfState(
                  matchedCountry.isoCode,
                  matchedState.isoCode
                );
                setCities(stateCities);
              }
            }
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlantTab]);

    // Sync form data when initialData changes (e.g., after save)
    useEffect(() => {
      // Check if initialData has actually changed
      const hasInitialDataChanged = JSON.stringify(initialData) !== JSON.stringify(originalData);
      
      if (hasInitialDataChanged && !hasChanged) {
        // Only sync if we're not in the middle of editing
        setForm({ ...initialData });
        setOriginalData({ ...initialData });

        // Handle country and state conversion
        if (initialData.country) {
          const countries = Country.getAllCountries();
          const matchedCountry = countries.find(
            (c) =>
              c.name === initialData.country || c.isoCode === initialData.country
          );

          if (matchedCountry) {
            setCountryCode(matchedCountry.isoCode);

            // Load states for this country
            const countryStates = State.getStatesOfCountry(
              matchedCountry.isoCode
            );
            setStates(countryStates);

            // Find matching state
            if (initialData.state) {
              const matchedState = countryStates.find(
                (s) =>
                  s.name === initialData.state || s.isoCode === initialData.state
              );

              if (matchedState) {
                setStateCode(matchedState.isoCode);

                // Load cities for this state
                const stateCities = City.getCitiesOfState(
                  matchedCountry.isoCode,
                  matchedState.isoCode
                );
                setCities(stateCities);
              }
            }
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);
    
    // Initialize country and state codes when entering edit mode
    useEffect(() => {
      if (editMode && form.country && !countryCode) {
        // Only initialize if we're in edit mode, have country data, but codes aren't set
        const countries = Country.getAllCountries();
        const matchedCountry = countries.find(
          (c) =>
            c.name === form.country || c.isoCode === form.country
        );

        if (matchedCountry) {
          setCountryCode(matchedCountry.isoCode);

          // Load states for this country
          const countryStates = State.getStatesOfCountry(
            matchedCountry.isoCode
          );
          setStates(countryStates);

          // Find matching state
          if (form.state) {
            const matchedState = countryStates.find(
              (s) =>
                s.name === form.state || s.isoCode === form.state
            );

            if (matchedState) {
              setStateCode(matchedState.isoCode);

              // Load cities for this state
              const stateCities = City.getCitiesOfState(
                matchedCountry.isoCode,
                matchedState.isoCode
              );
              setCities(stateCities);
            }
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode]);
    
    // Sync with external editMode prop
    useEffect(() => {
      if (editMode !== initialEditMode) {
        isSyncingFromPropsRef.current = true;
        setEditMode(initialEditMode);
      }
    }, [initialEditMode, editMode]);

    // Notify parent of edit mode changes - only when initiated by the component
    useEffect(() => {
      // Skip if we're syncing from props
      if (isSyncingFromPropsRef.current) {
        isSyncingFromPropsRef.current = false;
        return;
      }

      // Skip if change came from prop
      if (editMode === initialEditMode) return;

      if (onEditModeChange) {
        onEditModeChange(editMode);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, initialEditMode]);

    // Load states and cities when country/state changes
    useEffect(() => {
      if (countryCode) {
        const countryStates = State.getStatesOfCountry(countryCode);
        setStates(countryStates);
      }
    }, [countryCode]);

    useEffect(() => {
      if (stateCode && countryCode) {
        const stateCities = City.getCitiesOfState(countryCode, stateCode);
        setCities(stateCities);
      }
    }, [stateCode, countryCode]);

    // Add effect to validate postal code when country or postal code changes
    useEffect(() => {
      if (form.country && form.zipCode) {
        // Get country code from country name if needed
        let countryCode = form.country;
        if (!Country.getCountryByCode(form.country)) {
          const countryObj = Country.getAllCountries().find(
            (c) => c.name.toLowerCase() === String(form.country).toLowerCase()
          );
          countryCode = countryObj?.isoCode || form.country;
        }

        const result = postalCodes.validate(countryCode, form.zipCode);
        setPostalCodeError(result === true ? "" : result);
      } else {
        setPostalCodeError("");
      }
    }, [form.country, form.zipCode]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => form,
      getPayload: () => ({
        vendorMosymphonyID,
        brandOwnerMosymphonyID,
        updatePlantDetailsFlag: true,
        cmoSites: [
          {
            siteDetails: {
              siteID: plantInfo?.siteID,
              siteShortName: plantInfo?.siteShortName,
              markForDelete: false,
            },
            manufacturingPlantDataList: [
              {
                mfgPlantDetails: {
                  mfgPlantID: plantInfo?.manufacturingPlantID,
                  mfgPlantShortName: plantInfo?.manufacturingPlantShortName,
                  aslNumber: null,
                  markForDelete: false,
                },
                pickupAddress: {
                  addressLine1: form.addressLine1,
                  addressLine2: form.addressLine2,
                  city: form.city,
                  state: form.state,
                  country: form.country,
                  zipCode: form.zipCode,
                },
              },
            ],
          },
        ],
      }),
      validateForm: validate,
      // Also expose the hasChanged status to parent
      hasChanges: () => hasChanged,
      resetForm: () => {
        // Create a fresh copy of initialData to avoid any reference issues
        const cleanInitialData = JSON.parse(JSON.stringify(initialData));
        setForm(cleanInitialData);
        setOriginalData(cleanInitialData); // Update original data reference
        setErrors({});
        setHasChanged(false); // Reset the change tracking flag

        // Reset country and state codes based on initial data
        if (initialData.country) {
          const countries = Country.getAllCountries();
          const matchedCountry = countries.find(
            (c) =>
              c.name === initialData.country ||
              c.isoCode === initialData.country
          );

          if (matchedCountry) {
            setCountryCode(matchedCountry.isoCode);

            // Find matching state
            if (initialData.state) {
              const countryStates = State.getStatesOfCountry(
                matchedCountry.isoCode
              );
              const matchedState = countryStates.find(
                (s) =>
                  s.name === initialData.state ||
                  s.isoCode === initialData.state
              );

              if (matchedState) {
                setStateCode(matchedState.isoCode);
              }
            }
          }
        }
      },
    }));

    const handlePostalCodeChange = (e) => {
      // Apply consistent formatting to match the provided code
      // Only allow alphanumeric characters, spaces and hyphens
      // Replace multiple consecutive spaces or hyphens with a single one
      let value = e.target.value
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/(\s{2,}|-{2,})/g, (match) => match[0]);

      // Limit to 12 characters (standard for most postal codes worldwide)
      if (value.length > 12) {
        value = value.substring(0, 12);
      }

      // Update the form state
      setForm((prev) => ({ ...prev, zipCode: value }));

      // Clear zipCode error if it exists
      if (errors.zipCode) {
        setErrors((prev) => ({ ...prev, zipCode: "" }));
      }

      // Clear postalCodeError since we're changing the value
      setPostalCodeError("");

      // Validate against country-specific format if country is selected
      if (form.country && value) {
        // Get country code from country name if needed
        let countryCode = form.country;
        if (!Country.getCountryByCode(form.country)) {
          const countryObj = Country.getAllCountries().find(
            (c) => c.name.toLowerCase() === String(form.country).toLowerCase()
          );
          countryCode = countryObj?.isoCode || form.country;
        }

        const result = postalCodes.validate(countryCode, value);
        setPostalCodeError(result === true ? "" : result);
      }

      // Check if this is actually a change from the original data
      const updatedForm = { ...form, zipCode: value };
      const isChanged = checkForChanges(updatedForm);
      setHasChanged(isChanged);

      if (onChange) {
        onChange(updatedForm, isChanged);
      }
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      let updatedForm;

      if (name === "country") {
        setCountryCode(value);
        setStateCode("");
        // Get country name for the form
        const selectedCountry = Country.getCountryByCode(value);

        updatedForm = {
          ...form,
          country: selectedCountry?.name || value,
          state: "", // Reset state when country changes
          city: "", // Reset city when country changes
        };

        setForm(updatedForm);

        // Check if this is actually a change from the original data
        const isChanged = checkForChanges(updatedForm);
        setHasChanged(isChanged);

        if (onChange) {
          onChange(updatedForm, isChanged);
        }
      } else if (name === "state") {
        setStateCode(value);
        // Get state name for the form
        const selectedState = State.getStateByCodeAndCountry(
          value,
          countryCode
        );

        updatedForm = {
          ...form,
          state: selectedState?.name || value,
          city: "", // Reset city when state changes
        };

        setForm(updatedForm);

        // Check if this is actually a change from the original data
        const isChanged = checkForChanges(updatedForm);
        setHasChanged(isChanged);

        if (onChange) {
          onChange(updatedForm, isChanged);
        }
      } else {
        updatedForm = { ...form, [name]: value };
        setForm(updatedForm);

        // Check if this is actually a change from the original data
        const isChanged = checkForChanges(updatedForm);
        setHasChanged(isChanged);

        if (onChange) {
          onChange(updatedForm, isChanged);
        }
      }

      // Clear related field errors when parent field changes
      if (name === "country") {
        setErrors((prev) => ({ ...prev, state: "", city: "" }));
      } else if (name === "state") {
        setErrors((prev) => ({ ...prev, city: "" }));
      }

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    };

    const validate = () => {
      let valid = true;
      let errs = {};

      if (!form.addressLine1) {
        errs.addressLine1 = "Street Address 1 required";
        valid = false;
      } else if (
        !/^[A-Za-zÀ-ÿ0-9\s&\.,\-\/()]{1,255}$/.test(form.addressLine1)
      ) {
        errs.addressLine1 = "Invalid Address 1";
        valid = false;
      }
      if (!form.addressLine2) {
        errs.addressLine2 = "Street Address 2 required";
        valid = false;
      } else if (
        !/^[A-Za-zÀ-ÿ0-9\s&\.,\-\/()]{1,255}$/.test(form.addressLine2)
      ) {
        errs.addressLine2 = "Invalid Address 2";
        valid = false;
      }
      if (!form.country) {
        errs.country = "Country required";
        valid = false;
      }
      if (!form.state) {
        errs.state = "State required";
        valid = false;
      }
      if (!form.city) {
        errs.city = "City required";
        valid = false;
      }
      if (!form.zipCode) {
        errs.zipCode = "Post Code required";
        valid = false;
      } else if (form.country && postalCodeError) {
        // Add validation check for postalCodeError state (country-specific validation)
        errs.zipCode = postalCodeError;
        valid = false;
      } else if (!/^[a-zA-Z0-9\s-]{1,12}$/.test(form.zipCode)) {
        errs.zipCode = "Post Code can only contain letters, numbers, spaces, and hyphens (max 12 characters)";
        valid = false;
      }

      setErrors(errs);
      return valid;
    };

    // Function to check if form data has changed from original data
    const checkForChanges = (currentForm) => {
      // Directly compare each field that's relevant for this form
      const relevantFields = [
        "addressLine1",
        "addressLine2",
        "city",
        "state",
        "country",
        "zipCode",
      ];

      for (const field of relevantFields) {
        // Convert to strings for comparison (handles numbers vs string issues)
        const origValue = String(originalData[field] || "");
        const currentValue = String(currentForm[field] || "");

        if (origValue !== currentValue) {
          return true;
        }
      }

      return false;
    };



    // Edit mode
    return (
      <div className="cmo-stepper-wrapper plant-details-step">
        <div className="stepper-container">
          <form autoComplete="off">

            <Grid className="two-col-forms-grid" container spacing={3}>
              <Grid item className="multiple-line-text" xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="addressLine1">
                    Street Address 1 {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? <Typography>{form.addressLine1 || "-"}</Typography> : <TextField
                    id="addressLine1"
                    name="addressLine1"
                    value={form.addressLine1 || ""}
                    onChange={handleChange}
                    onBlur={(e) => {
                      const { name, value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (typeof value === "string" && value.trim() !== value) {
                        handleChange({ target: { name, value: value.trim() } });
                      }
                    }}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    maxRows={10}
                    error={!!errors.addressLine1}
                    helperText={errors.addressLine1 || ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={{
                      ...commonInlineStyles.inputProps,
                      maxLength: 255,
                    }}
                    style={commonInlineStyles.input}
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/[^a-zA-ZÀ-ÿ0-9\s&.,\-/]/g, "")
                        .replace(
                          /(\s{2,}|&{2,}|,-{2,}|,{2,}|\.{2,}|\/{2,}|'{2,}|\({2,}|\){2,})/g,
                          (match) => match[0]
                        );
                    }}
                  />}
                </FormControl>
              </Grid>
              <Grid className="multiple-line-text" item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="addressLine2">
                    Street Address 2 {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {
                    !editMode ? <Typography>{form.addressLine2 || "-"}</Typography> : <TextField
                      id="addressLine2"
                      name="addressLine2"
                      value={form.addressLine2 || ""}
                      onChange={handleChange}
                      onBlur={(e) => {
                        const { name, value } = e.target;
                        // Trim leading and trailing spaces for string values
                        if (typeof value === "string" && value.trim() !== value) {
                          handleChange({ target: { name, value: value.trim() } });
                        }
                      }}
                      variant="outlined"
                      placeholder="Enter text here"
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={10}
                      error={!!errors.addressLine2}
                      helperText={errors.addressLine2 || ""}
                      InputProps={{ style: commonInlineStyles.input }}
                      sx={inputFieldStyles}
                      inputProps={{
                        ...commonInlineStyles.inputProps,
                        maxLength: 255,
                      }}
                      style={commonInlineStyles.input}
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .replace(/[^a-zA-ZÀ-ÿ0-9\s&.,\-/]/g, "")
                          .replace(
                            /(\s{2,}|&{2,}|,-{2,}|,{2,}|\.{2,}|\/{2,}|'{2,}|\({2,}|\){2,})/g,
                            (match) => match[0]
                          );
                      }}
                    />
                  }
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="country">
                    Country {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {
                    !editMode ? <Typography>{form.country || "-"}</Typography> :
                      <Select
                        select
                        id="country"
                        name="country"
                        value={countryCode || ""}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        required
                        error={!!errors.country}
                        helperText={errors.country || ""}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select from the list
                        </MenuItem>
                        {Country.getAllCountries().map((country) => (
                          <MenuItem key={country.isoCode} value={country.isoCode}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                  }
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="state">
                    State {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {
                    !editMode ? <Typography>{form.state || "-"}</Typography> :
                      <Select
                        select
                        id="state"
                        name="state"
                        value={stateCode || ""}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        required
                        disabled={!countryCode}
                        error={!!errors.state}
                        helperText={errors.state || ""}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          Select from the list
                        </MenuItem>
                        {states.map((state) => (
                          <MenuItem key={state.isoCode} value={state.isoCode}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                  }
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="city">
                    City {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? <Typography>{form.city || "-"}</Typography> :
                    <Select
                      select
                      id="city"
                      name="city"
                      value={form.city || ""}
                      onChange={handleChange}
                      variant="outlined"
                      fullWidth
                      required
                      disabled={!stateCode}
                      error={!!errors.city}
                      helperText={errors.city || ""}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select from the list
                      </MenuItem>
                      {cities.map((city) => (
                        <MenuItem key={city.name} value={city.name}>
                          {city.name}
                        </MenuItem>
                      ))}
                      {form.city && !cities.some((c) => c.name === form.city) && (
                        <MenuItem key={form.city} value={form.city}>
                          {form.city}
                        </MenuItem>
                      )}
                    </Select>}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl className={classes.formField}>
                  <Typography component="label" htmlFor="zipCode">
                    Post Code {editMode && <span style={{ color: "red" }}>*</span>}
                  </Typography>
                  {!editMode ? <Typography>{form.zipCode || "-"}</Typography> : <TextField
                    id="zipCode"
                    name="zipCode"
                    value={form.zipCode || ""}
                    onChange={handlePostalCodeChange}
                    onBlur={(e) => {
                      const { name, value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (typeof value === "string" && value.trim() !== value) {
                        handlePostalCodeChange({ target: { name, value: value.trim() } });
                      }
                    }}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    required
                    error={!!errors.zipCode || !!postalCodeError}
                    helperText={errors.zipCode || postalCodeError || ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={{
                      ...commonInlineStyles.inputProps,
                      maxLength: 12,
                    }}
                    style={commonInlineStyles.input}
                  />}
                </FormControl>
              </Grid>
            </Grid>
          </form>
          <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />
        </div>
      </div>
    );
  }
);

export default PickupAddressSection;
